import { useEffect, useState, type ComponentProps, type FocusEvent } from 'react'

import {
  BASE_CURRENCY,
  currencySymbol,
  formatMoneyForInput,
  isPartialMoneyInput,
  parseMoneyInput,
  type Pesewas,
} from '@/lib/money'
import { cn } from '@/lib/utils'

export interface MoneyInputProps
  extends Omit<ComponentProps<'input'>, 'value' | 'onChange' | 'onBlur' | 'type'> {
  /** The amount in integer minor units. `null` means the field is empty, which is not zero. */
  value: Pesewas | null
  onChange: (value: Pesewas | null) => void
  currency?: string
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void
}

/**
 * Cash entry for GHS.
 *
 * The value handed to `onChange` is always an integer count of pesewas. The text the user types is
 * parsed by splitting the whole and fractional parts and combining them as integers — no
 * `parseFloat`, no multiplication of a decimal by 100 — so a keyed 1234.56 is exactly 123456
 * pesewas and cannot arrive at the ledger a pesewa light.
 *
 * Deliberately absent: arrow-key stepping. On a form that posts money, a stray key must never
 * change an amount the operator has already counted at the counter.
 */
export function MoneyInput({
  value,
  onChange,
  currency = BASE_CURRENCY,
  className,
  disabled,
  onBlur,
  ...props
}: MoneyInputProps) {
  const [text, setText] = useState(() => (value === null ? '' : formatMoneyForInput(value)))
  const [isFocused, setIsFocused] = useState(false)

  // While the field has focus the user's own keystrokes are authoritative — reformatting under a
  // moving caret is how digits get eaten. Outside focus the field mirrors the model.
  useEffect(() => {
    if (!isFocused) {
      setText(value === null ? '' : formatMoneyForInput(value))
    }
  }, [value, isFocused])

  return (
    <div
      className={cn(
        'flex h-9 w-full items-center rounded-control border border-border-subtle bg-surface transition-colors',
        'focus-within:border-brand-text hover:border-border-strong',
        disabled && 'cursor-not-allowed bg-surface-muted',
        props['aria-invalid'] && 'border-danger-fill',
        className,
      )}
    >
      <span
        className="numeric pointer-events-none pl-3 text-xs font-medium text-muted select-none"
        aria-hidden
      >
        {currencySymbol(currency)}
      </span>
      <input
        {...props}
        type="text"
        inputMode="decimal"
        autoComplete="off"
        spellCheck={false}
        disabled={disabled}
        value={text}
        onChange={(event) => {
          const next = event.target.value
          // Reject the keystroke rather than the finished value: a lone minus or a trailing
          // decimal point is a legitimate half-typed amount.
          if (!isPartialMoneyInput(next)) {
            return
          }

          setText(next)

          const parsed = parseMoneyInput(next)
          if (parsed.ok) {
            onChange(parsed.value)
          } else if (parsed.reason === 'empty') {
            onChange(null)
          }
        }}
        onFocus={(event) => {
          setIsFocused(true)
          event.target.select()
        }}
        onBlur={(event) => {
          setIsFocused(false)

          const parsed = parseMoneyInput(text)
          if (parsed.ok) {
            setText(formatMoneyForInput(parsed.value))
            onChange(parsed.value)
          } else {
            setText('')
            onChange(null)
          }

          onBlur?.(event)
        }}
        className={cn(
          'numeric h-full w-full bg-transparent px-2 text-right text-sm text-heading tabular-nums outline-none',
          'placeholder:text-muted disabled:cursor-not-allowed disabled:text-muted',
        )}
      />
    </div>
  )
}
